import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import {
  toast,
  fullloader,
  updateAudienceFromDraft,
} from '../../../redux/action';
import {
  updateDraft,
  getDraftBy_id,
  saveNewDraftService,
  updateHTMLbasedOnDraftId,
} from '../../../redux/services';
import useSaveStatus from '../Components/SavingStatus';
import {
  selectedOrganizationSelector,
  userDetailSelector,
  audienceSelector,
} from '../../../redux/selector';
import { DraftHeader } from '../Components/Header';
import BaseEditor from '../BaseEditor';
import { removeExtraFieldsFromStandAloneAudienceForDraft } from '../../_shared/utils';

const DraftEditor = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const draftId = queryString.parse(location.search).draft;
  const [
    saveStatus,
    isTextStatusSaved,
    { onStartSavingStatus, onFinishSavingStatus, onMarkChangesUnsavedStatus },
  ] = useSaveStatus();
  const [design, setDesign] = useState(null);

  const organizationId = useSelector(selectedOrganizationSelector).id;
  const userId = useSelector(userDetailSelector).id;
  const audience = useSelector(audienceSelector).audience || {};
  const [draft, setDraft] = useState({});

  const updateDraftBasedOnId = async shouldLoadDraftsPage => {
    await updateHTMLbasedOnDraftId(draftId);
    if (shouldLoadDraftsPage) {
      history.push('/dashboard/drafts');
    }
  };

  // Detect route changes and trigger updateDraftBasedOnId function
  useEffect(() => {
    const unlisten = history.listen(newLocation => {
      // Check if the user is navigating away from the current page
      if (location.pathname !== newLocation.pathname) {
        if (
          newLocation.pathname == '/dashboard/drafts' &&
          newLocation.search == ''
        ) {
          updateDraftBasedOnId(true);
          history.replace('/loading');
        } else {
          updateDraftBasedOnId();
        }
      }
    });

    return () => {
      unlisten(); // Cleanup the listener
    };
  }, [history, location, draftId]);

  const loadDraft = async () => {
    try {
      dispatch(fullloader('true', 'Loading draft.'));
      const fetchedDraft = await getDraftBy_id(draftId);

      const parsedDesign =
        typeof fetchedDraft.design === 'string'
          ? JSON.parse(fetchedDraft.design)
          : fetchedDraft.design;
      setDesign(parsedDesign || {});
      setDraft(fetchedDraft);

      dispatch(fullloader(false));
      dispatch(updateAudienceFromDraft(fetchedDraft));
      dispatch(toast('success', `Loaded ${fetchedDraft.name}`));
    } catch (e) {
      console.error(e);
      dispatch(toast('error', 'Error fetching draft'));
      history.replace('/dashboard/drafts');
    }
  };

  const saveNewDraft = async (data = {}) => {
    try {
      setDesign(data.design || {});
      const result = await saveNewDraftService({
        design: '{}',
        organizationId,
        userId,
        ...data,
        ...removeExtraFieldsFromStandAloneAudienceForDraft(audience),
      });

      history.replace(`/dashboard/sendmessage?draft=${result._id}`);
      setDraft(result);
      dispatch(toast('success', 'Loaded a new draft'));
    } catch (e) {
      dispatch(toast('error', 'Error while saving a new draft'));
    }
  };

  const handleDraftSave = async data => {
    onStartSavingStatus();
    if (draftId === 'new') {
      await saveNewDraft(data);
    } else {
      await updateDraft({ _id: draftId, ...data });
    }
    onFinishSavingStatus();
  };

  useEffect(() => {
    if (draftId === 'new') {
      setDesign({});
    } else {
      loadDraft();
    }
  }, [draftId]);

  return (
    <BaseEditor
      Header={DraftHeader}
      data={draft}
      onSave={handleDraftSave}
      saveStatus={saveStatus}
      isTextStatusSaved={isTextStatusSaved}
      onUnsavedStatus={onMarkChangesUnsavedStatus}
      design={design}
      setDesign={setDesign}
      setDraft={setDraft}
    />
  );
};

export default DraftEditor;
